import React, { useState, useEffect } from "react";
import { FaCloud, FaCloudRain, FaCloudSun, FaSun, FaSnowflake, FaWind, FaBolt, FaSmog, FaQuestion } from "react-icons/fa";

// Inline styles
const styles = {
  weatherForecastContainer: {
    marginBottom: '2rem',
    backgroundColor: 'rgba(20, 83, 116, 0.2)',
    borderRadius: '12px',
    padding: '1.5rem'
  },
  weatherCardContainer: {
    display: 'flex',
    gap: '16px',
    overflowX: 'auto',
    paddingBottom: '16px',
    scrollBehavior: 'smooth',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'thin',
    scrollbarColor: '#1bb0b0 rgba(255, 255, 255, 0.1)'
  },
  weatherSection: {
    marginBottom: '1rem'
  },
  sectionTitle: {
    fontSize: '16px',
    color: '#1bb0b0',
    marginBottom: '10px'
  },
  weatherCard: {
    flex: '0 0 auto',
    width: '125px',
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    borderRadius: '8px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    transition: 'transform 0.2s, background-color 0.2s',
    cursor: 'default',
    height: '230px',
    justifyContent: 'space-between'
  },
  weatherCardHover: {
    transform: 'translateY(-5px)',
    backgroundColor: 'rgba(255, 255, 255, 0.12)'
  },
  dayCard: {
    borderTop: '3px solid #1bb0b0'
  },
  nightCard: {
    borderTop: '3px solid #5D5C7C'
  },
  weatherDay: {
    fontWeight: 600,
    marginBottom: '12px',
    fontSize: '14px',
    color: '#d6e1ec',
    textAlign: 'center',
    height: '20px',
    display: 'flex',
    alignItems: 'center'
  },
  weatherIcon: {
    fontSize: '32px',
    marginBottom: '12px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  sunIcon: {
    color: '#FFD700' // Gold/yellow for sun
  },
  cloudIcon: {
    color: '#E0E0E0' // Light gray for clouds
  },
  cloudSunIcon: {
    color: '#FFD700' // Gold/yellow for partly cloudy
  },
  rainIcon: {
    color: '#87CEFA' // Light blue for rain
  },
  snowIcon: {
    color: '#FFFFFF' // White for snow
  },
  windIcon: {
    color: '#D3D3D3' // Light gray for wind
  },
  thunderIcon: {
    color: '#FFD700' // Gold/yellow for lightning
  },
  fogIcon: {
    color: '#D3D3D3' // Light gray for fog
  },
  weatherTemp: {
    fontSize: '22px',
    fontWeight: 700,
    marginBottom: '10px',
    color: '#fff',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  weatherDesc: {
    fontSize: '12px',
    textAlign: 'center',
    marginBottom: '12px',
    color: '#d6e1ec',
    height: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden'
  },
  weatherWind: {
    fontSize: '12px',
    color: '#d6e1ec',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '500'
  },
  weatherWindHighlight: {
    color: '#1bb0b0',
    fontWeight: 'bold'
  },
  weatherLoading: {
    textAlign: 'center',
    padding: '20px',
    color: '#d6e1ec'
  },
  weatherError: {
    textAlign: 'center',
    padding: '20px',
    color: '#d6e1ec'
  },
  weatherDebug: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    padding: '10px',
    borderRadius: '5px',
    marginBottom: '10px',
    color: '#d6e1ec',
    fontSize: '14px'
  },
  weatherFooter: {
    marginTop: '16px',
    textAlign: 'center'
  },
  weatherAttribution: {
    fontSize: '12px',
    color: '#d6e1ec',
    opacity: 0.6
  },
  windChartContainer: {
    marginTop: '1.5rem',
    padding: '1rem 0.5rem',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '8px',
    height: 'auto',
    marginBottom: '1.5rem'
  },
  windChartTitle: {
    fontSize: '16px',
    color: '#d6e1ec',
    marginBottom: '10px',
    textAlign: 'center',
    fontFamily: 'Geist, sans-serif'
  },
  windChartDescription: {
    fontSize: '13px',
    color: '#d6e1ec',
    opacity: 0.8,
    marginBottom: '15px',
    textAlign: 'center',
    fontFamily: 'Geist, sans-serif',
    maxWidth: '600px',
    margin: '0 auto 15px auto'
  },
  windChartPlaceholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#d6e1ec',
    opacity: 0.7,
    height: '200px',
    fontSize: '14px'
  }
};

// Media query styles need to be handled differently with React inline styles
// We'll use window width to determine mobile styles

const WeatherForecast = ({ latitude, longitude }) => {
  console.log('WeatherForecast component received props:', { latitude, longitude });
  
  const [forecastData, setForecastData] = useState(null);
  const [yesterdayData, setYesterdayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredCardIndex, setHoveredCardIndex] = useState(null);
  const [windData, setWindData] = useState(null);
  const [isBrowser, setIsBrowser] = useState(false);
  const windChartRef = React.useRef(null);
  const [windChartCreated, setWindChartCreated] = useState(false);
  const [debugInfo, setDebugInfo] = useState({});

  // Check if we're in the browser
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsBrowser(true);
      
      // Check if Plotly is already available
      if (window.Plotly) {
        console.log('Plotly already available on initial load');
        setDebugInfo(prev => ({
          ...prev,
          isBrowser: true,
          plotlyAvailable: true,
          plotlyVersion: window.Plotly.version,
          windowWidth: window.innerWidth,
          windowHeight: window.innerHeight
        }));
      } else {
        // Otherwise, we'll wait for it to load via the script tag
        console.log('Waiting for Plotly to load...');
        setDebugInfo(prev => ({
          ...prev,
          isBrowser: true,
          plotlyAvailable: false,
          windowWidth: window.innerWidth,
          windowHeight: window.innerHeight
        }));
        
        // Add an event listener for when the script loads
        const handlePlotlyLoad = () => {
          if (window.Plotly) {
            console.log('Plotly loaded dynamically!');
            setDebugInfo(prev => ({
              ...prev,
              plotlyAvailable: true,
              plotlyVersion: window.Plotly.version
            }));
          }
        };
        
        // Check every 500ms for Plotly for up to 10 seconds (20 attempts)
        let attempts = 0;
        const checkInterval = setInterval(() => {
          if (window.Plotly) {
            handlePlotlyLoad();
            clearInterval(checkInterval);
          } else if (++attempts >= 20) {
            console.error('Failed to load Plotly after 10 seconds');
            clearInterval(checkInterval);
            setDebugInfo(prev => ({
              ...prev,
              plotlyLoadFailed: true
            }));
          }
        }, 500);
        
        return () => clearInterval(checkInterval);
      }
    }
  }, []);

  // Create wind chart when data is available
  useEffect(() => {
    let cleanupFunction = null;
    
    if (isBrowser && windData && windData.length > 0 && windChartRef.current && !windChartCreated) {
      // We'll wait for the window.Plotly global to be available
      setDebugInfo(prev => ({
        ...prev,
        windDataLength: windData.length,
        windChartRefExists: !!windChartRef.current,
        createChartAttempt: true
      }));
      
      const checkPlotly = () => {
        if (window.Plotly) {
          setDebugInfo(prev => ({
            ...prev,
            plotlyFound: true
          }));
          try {
            cleanupFunction = createWindChart();
            setWindChartCreated(true);
          } catch (err) {
            console.error('Error initializing wind chart:', err);
          }
        } else {
          // If Plotly isn't available yet, check again in a moment
          setDebugInfo(prev => ({
            ...prev,
            plotlyCheckAttempts: (prev.plotlyCheckAttempts || 0) + 1
          }));
          setTimeout(checkPlotly, 500);
        }
      };
      
      checkPlotly();
    }

    // Cleanup
    return () => {
      try {
        // Call the cleanup function returned by createWindChart if it exists
        if (typeof cleanupFunction === 'function') {
          cleanupFunction();
        }
        
        // Always try to purge the chart when unmounting
        if (windChartRef.current && window.Plotly) {
          window.Plotly.purge(windChartRef.current);
        }
        
        setWindChartCreated(false);
      } catch (err) {
        console.warn('Error during chart cleanup:', err);
      }
    };
  }, [isBrowser, windData, windChartCreated]);

  // Handle responsive UI
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setDebugInfo(prev => ({
        ...prev,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight
      }));
      
      // Redraw the chart when window is resized
      if (windChartCreated && windChartRef.current && window.Plotly) {
        try {
          if (document.body.contains(windChartRef.current)) {
            window.Plotly.Plots.resize(windChartRef.current);
          }
        } catch (err) {
          console.warn('Error resizing chart during window resize:', err);
        }
      }
    };

    // Check on initial load
    if (typeof window !== 'undefined') {
      handleResize();
      window.addEventListener('resize', handleResize);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, [windChartCreated]);

  useEffect(() => {
    const fetchWeatherData = async () => {
      // Debug log for coordinates
      console.log('Fetching weather data with coordinates:', { latitude, longitude });
      setDebugInfo(prev => ({ 
        ...prev, 
        fetchAttempt: true,
        coordinates: { latitude, longitude } 
      }));
      
      if (!latitude || !longitude) {
        setError("Missing coordinates");
        setLoading(false);
        setDebugInfo(prev => ({ ...prev, error: "Missing coordinates" }));
        return;
      }

      try {
        setLoading(true);
        // Step 1: Get the forecast URL from the points endpoint
        const pointsUrl = `https://api.weather.gov/points/${latitude},${longitude}`;
        setDebugInfo(prev => ({ ...prev, pointsUrl }));
        console.log('Fetching from points URL:', pointsUrl);
        
        const pointsResponse = await fetch(pointsUrl);
        
        if (!pointsResponse.ok) {
          const errorText = await pointsResponse.text();
          throw new Error(`Failed to get metadata: ${pointsResponse.statusText}. Details: ${errorText}`);
        }
        
        const pointsData = await pointsResponse.json();
        setDebugInfo(prev => ({ 
          ...prev, 
          pointsDataReceived: true,
          officeId: pointsData.properties.cwa,
          gridX: pointsData.properties.gridX,
          gridY: pointsData.properties.gridY
        }));
        
        // Step 2: Get the forecast data
        const forecastUrl = pointsData.properties.forecast;
        setDebugInfo(prev => ({ ...prev, forecastUrl }));
        console.log('Fetching from forecast URL:', forecastUrl);
        
        const forecastResponse = await fetch(forecastUrl);
        
        if (!forecastResponse.ok) {
          const errorText = await forecastResponse.text();
          throw new Error(`Failed to get forecast: ${forecastResponse.statusText}. Details: ${errorText}`);
        }
        
        const forecastData = await forecastResponse.json();
        setDebugInfo(prev => ({ ...prev, forecastDataReceived: true }));
        
        // Process forecast data and extract wind data
        const processedForecast = processForecastData(forecastData);
        setDebugInfo(prev => ({ 
          ...prev, 
          processedForecastLength: processedForecast.length,
          firstPeriodName: processedForecast[0]?.name || 'N/A' 
        }));
        
        setForecastData(processedForecast);
        
        // Extract wind data for the chart
        const windDataArray = processedForecast.map(period => {
          // Parse wind speed (e.g., "10 mph" → 10)
          const windSpeedMatch = period.windSpeed.match(/(\d+)/);
          const windSpeedValue = windSpeedMatch ? parseInt(windSpeedMatch[0], 10) : 0;
          
          return {
            name: period.name,
            windSpeed: windSpeedValue,
            windDirection: period.windDirection,
            forecast: period.shortForecast
          };
        });
        
        setWindData(windDataArray);
        
        // Fetch yesterday's data (we'll use the observation stations API)
        // First, get nearest station from the points data
        const observationStationsUrl = pointsData.properties.observationStations;
        setDebugInfo(prev => ({ ...prev, observationStationsUrl }));
        const stationsResponse = await fetch(observationStationsUrl);
        
        if (!stationsResponse.ok) {
          // Don't throw an error, just log it - yesterday's data is optional
          console.warn("Failed to get observation stations");
          setDebugInfo(prev => ({ ...prev, stationsError: stationsResponse.statusText }));
        } else {
          const stationsData = await stationsResponse.json();
          
          if (stationsData.features && stationsData.features.length > 0) {
            // Get observations from the nearest station
            const nearestStation = stationsData.features[0].properties.stationIdentifier;
            setDebugInfo(prev => ({ ...prev, nearestStation }));
            
            const observationsUrl = `https://api.weather.gov/stations/${nearestStation}/observations/latest`;
            setDebugInfo(prev => ({ ...prev, observationsUrl }));
            
            const observationsResponse = await fetch(observationsUrl);
            
            if (observationsResponse.ok) {
              const observationsData = await observationsResponse.json();
              setDebugInfo(prev => ({ ...prev, observationsDataReceived: true }));
              
              // Make sure we have valid temperature data
              if (observationsData.properties.temperature.value !== null) {
                // Check the actual structure of the wind speed from the API
                const rawWindSpeed = observationsData.properties.windSpeed.value;
                console.log('Raw wind speed from API:', rawWindSpeed);
                setDebugInfo(prev => ({ ...prev, rawWindSpeed }));
                
                // Process yesterday's data
                // Format wind speed properly based on its type
                let formattedWindSpeed;
                if (typeof rawWindSpeed === 'number') {
                  // If it's a number, convert to string format (e.g. "10 mph")
                  formattedWindSpeed = `${Math.round(rawWindSpeed)} mph`;
                } else if (typeof rawWindSpeed === 'string') {
                  // If it's already a string, use as is
                  formattedWindSpeed = rawWindSpeed;
                } else {
                  // If undefined or null, use default
                  formattedWindSpeed = "0 mph";
                }
                
                // Format a time for yesterday's data point
                const yesterdayDate = new Date(observationsData.properties.timestamp);
                let yesterdayChartTime;
                const yesterdayHour = yesterdayDate.getHours();
                if (yesterdayHour >= 0 && yesterdayHour < 6) {
                  yesterdayChartTime = "12 AM";
                } else if (yesterdayHour >= 6 && yesterdayHour < 12) {
                  yesterdayChartTime = "6 AM";
                } else if (yesterdayHour >= 12 && yesterdayHour < 18) {
                  yesterdayChartTime = "12 PM";
                } else {
                  yesterdayChartTime = "6 PM";
                }
                
                const yesterday = {
                  name: yesterdayChartTime,
                  displayTime: `Yesterday ${yesterdayChartTime}`,
                  chartTime: yesterdayChartTime,
                  temperature: Math.round(observationsData.properties.temperature.value * 9/5 + 32),
                  shortForecast: observationsData.properties.textDescription || "No data",
                  windSpeed: formattedWindSpeed,
                  windDirection: observationsData.properties.windDirection?.value || "N/A",
                  icon: getWeatherIcon(observationsData.properties.textDescription),
                  isDaytime: true
                };
                
                setYesterdayData(yesterday);
                setDebugInfo(prev => ({ ...prev, yesterdayDataProcessed: true, formattedWindSpeed }));
                
                // Add yesterday's wind data to the array
                if (windDataArray.length > 0) {
                  // Extract numeric value safely
                  let windSpeedValue = 0;
                  
                  if (typeof rawWindSpeed === 'number') {
                    // If it's already a number, round it
                    windSpeedValue = Math.round(rawWindSpeed);
                  } else if (typeof rawWindSpeed === 'string') {
                    // If it's a string, try to extract the number
                    const matches = rawWindSpeed.match(/(\d+)/);
                    windSpeedValue = matches ? parseInt(matches[1], 10) : 0;
                  }
                  
                  windDataArray.unshift({
                    name: "Yesterday",
                    displayTime: `Yesterday ${yesterdayChartTime}`,
                    chartTime: yesterdayChartTime,
                    windSpeed: windSpeedValue,
                    windDirection: yesterday.windDirection,
                    forecast: yesterday.shortForecast
                  });
                  
                  setWindData(windDataArray);
                }
              }
            } else {
              setDebugInfo(prev => ({ 
                ...prev, 
                observationsError: observationsResponse.statusText 
              }));
            }
          } else {
            setDebugInfo(prev => ({ ...prev, noStationsFound: true }));
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Error fetching weather data:", err);
        setError(err.message);
        setLoading(false);
        setDebugInfo(prev => ({ ...prev, fetchError: err.message }));
      }
    };

    fetchWeatherData();
  }, [latitude, longitude]);

  const processForecastData = (data) => {
    // Extract the periods from the forecast data
    const originalPeriods = data.properties.periods.slice(0, 14); // Get more periods to work with
    
    // Create a normalized forecast with consistent 6-hour intervals
    const now = new Date();
    // Round to the nearest 6-hour interval (0, 6, 12, 18)
    let startHour = Math.floor(now.getHours() / 6) * 6;
    
    // Create array of 7 forecast periods at 6-hour intervals
    const normalizedForecast = [];
    
    for (let i = 0; i < 7; i++) {
      // Calculate the date and time for this interval
      const forecastDate = new Date(now);
      forecastDate.setHours(startHour + (i * 6), 0, 0, 0);
      
      // Format the time (6 AM, 12 PM, 6 PM, 12 AM)
      let timeLabel;
      const hour = forecastDate.getHours();
      if (hour === 0) {
        timeLabel = '12 AM';
      } else if (hour === 12) {
        timeLabel = '12 PM';
      } else if (hour === 6) {
        timeLabel = '6 AM';
      } else if (hour === 18) {
        timeLabel = '6 PM';
      }
      
      // Determine day label
      let dayLabel = '';
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      if (forecastDate.toDateString() === today.toDateString()) {
        dayLabel = 'Today';
      } else if (forecastDate.toDateString() === tomorrow.toDateString()) {
        dayLabel = 'Tomorrow';
      } else {
        // Use abbreviated day of week for other days
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        dayLabel = days[forecastDate.getDay()];
      }
      
      // Find the best matching forecast period from the original data
      // Look for a period whose start time is closest to our desired interval
      let bestMatch = null;
      let smallestTimeDiff = Infinity;
      
      for (const period of originalPeriods) {
        const periodStartTime = new Date(period.startTime);
        const timeDiff = Math.abs(periodStartTime - forecastDate);
        
        if (timeDiff < smallestTimeDiff) {
          smallestTimeDiff = timeDiff;
          bestMatch = period;
        }
      }
      
      if (bestMatch) {
        normalizedForecast.push({
          ...bestMatch,
          name: `${timeLabel}`, // Just show the time
          displayTime: `${dayLabel} ${timeLabel}`, // Full display with day and time
          chartTime: timeLabel, // Chart label - just the time
          originalName: bestMatch.name, // Keep the original name for reference
          originalStartTime: bestMatch.startTime, // Keep the original time
          normalizedTime: forecastDate.toISOString() // Store the normalized time
        });
      }
    }
    
    return normalizedForecast;
  };

  const getWeatherIcon = (forecast) => {
    const forecastLower = forecast?.toLowerCase() || '';
    
    if (forecastLower.includes("thunderstorm")) {
      return <FaBolt style={styles.thunderIcon} />;
    }
    if (forecastLower.includes("rain") || forecastLower.includes("showers")) {
      return <FaCloudRain style={styles.rainIcon} />;
    }
    if (forecastLower.includes("snow") || forecastLower.includes("flurries")) {
      return <FaSnowflake style={styles.snowIcon} />;
    }
    if (forecastLower.includes("fog") || forecastLower.includes("haze")) {
      return <FaSmog style={styles.fogIcon} />;
    }
    if (forecastLower.includes("wind")) {
      return <FaWind style={styles.windIcon} />;
    }
    if (forecastLower.includes("mostly cloudy") || forecastLower.includes("cloudy")) {
      return <FaCloud style={styles.cloudIcon} />;
    }
    if (forecastLower.includes("partly cloudy") || forecastLower.includes("partly sunny")) {
      return <FaCloudSun style={styles.cloudSunIcon} />;
    }
    if (forecastLower.includes("clear") || forecastLower.includes("sunny")) {
      return <FaSun style={styles.sunIcon} />;
    }
    
    // Default icon if none of the above match
    return <FaQuestion style={styles.cloudIcon} />;
  };

  const formatWindSpeed = (speed) => {
    // Handle different types of speed input
    if (!speed) return "0 mph";
    
    // If it's already a number, convert directly
    if (typeof speed === 'number') {
      return `${Math.round(speed)} mph`;
    }
    
    // For string format
    if (typeof speed === 'string') {
      // Extract the numeric value from wind speed string (e.g., "10 to 15 mph" -> "10-15")
      const matches = speed.match(/(\d+)(?:\s+to\s+(\d+))?/);
      if (!matches) return speed;
      
      if (matches[2]) {
        // Range format
        return `${matches[1]}-${matches[2]} mph`;
      } else {
        // Single value format
        return `${matches[1]} mph`;
      }
    }
    
    // Fallback for unknown types
    return "Unknown speed";
  };

  const createWindChart = () => {
    if (!windData || !windData.length || !windChartRef.current || !window.Plotly) {
      console.log('Cannot create wind chart - missing dependencies');
      return;
    }
    
    // Simple chronological ordering based on the normalized time
    const sortedWindData = [...windData].sort((a, b) => {
      // Sort by the normalized time if available
      if (a.normalizedTime && b.normalizedTime) {
        return new Date(a.normalizedTime) - new Date(b.normalizedTime);
      }
      return 0;
    });
    
    // Limit to 7 data points maximum for clarity
    const limitedData = sortedWindData.slice(0, 7);
    
    // Create SEQUENTIAL numeric x values (0, 1, 2, 3...) for proper chart stretching
    const xValues = limitedData.map((_, index) => index);
    
    // Create descriptive labels for the x-axis ticks
    const tickLabels = limitedData.map(item => {
      if (!item.normalizedTime) return item.name;
      
      const date = new Date(item.normalizedTime);
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const dayName = days[date.getDay()];
      
      const hour = date.getHours();
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      
      // Return a clear day+hour format
      return `${dayName} ${hour12}${ampm}`;
    });
    
    const windSpeeds = limitedData.map(item => item.windSpeed);
    
    // Very simple line chart
    const data = [{
      x: xValues, // Use sequential values for x
      y: windSpeeds,
      type: 'scatter',
      mode: 'lines+markers',
      line: {
        color: '#1bb0b0',
        width: 3,
        shape: 'spline', // Make the line smooth
        smoothing: 1.2   // Adjust the smoothing factor
      },
      marker: {
        color: '#1bb0b0',
        size: 8,
        hoversize: 10 // Make markers slightly larger on hover
      },
      name: 'Wind Speed (mph)',
      hoverinfo: 'text',
      hovertext: limitedData.map(item => {
        const timeLabel = tickLabels[xValues.indexOf(xValues[limitedData.indexOf(item)])];
        return `<b>${timeLabel}</b><br><b>${item.windSpeed} mph</b><br>${item.forecast || ''}`; 
      }),
      hoverlabel: {
        bgcolor: 'rgba(20, 83, 116, 0.9)',
        bordercolor: '#1bb0b0',
        font: {
          family: 'Geist, sans-serif',
          size: 13,
          color: 'white'
        },
        padding: { l: 8, r: 8, t: 8, b: 8 }
      }
    }];
    
    // Calculate reasonable y-axis range based on data
    const maxWindSpeed = Math.max(...windSpeeds, 10); // At least 10 mph
    const yAxisMax = Math.ceil(maxWindSpeed / 5) * 5 + 5; // Round up to nearest 5 and add 5
    
    const layout = {
      // NO TITLE - we'll use the div title instead to avoid duplication
      autosize: true,
      height: 280, // Reduced from 300 to 280
      margin: { t: 10, r: 20, l: 40, b: 60 }, // Reduced bottom margin from 80 to 60
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      font: {
        family: 'Geist, sans-serif',
        color: '#d6e1ec'
      },
      hovermode: 'closest', // Show hover info for closest point
      xaxis: {
        tickmode: 'array',
        tickvals: xValues, // Use the sequential values
        ticktext: tickLabels, // But show the formatted time labels
        tickangle: -45,
        tickfont: { size: 11 }, // Reduced font size from 12 to 11
        gridcolor: 'rgba(255,255,255,0.1)',
        showgrid: true,
        fixedrange: true, // Disable zooming on x axis
        automargin: true  // Add automargin to ensure labels fit
      },
      yaxis: {
        title: {
          text: 'Wind Speed (mph)',
          font: {
            family: 'Geist, sans-serif',
            size: 14,
            color: '#d6e1ec'
          }
        },
        range: [0, yAxisMax],
        tickmode: 'linear',
        tick0: 0,
        dtick: 5,
        gridcolor: 'rgba(255,255,255,0.1)',
        fixedrange: true // Disable zooming on y axis
      }
    };
    
    const config = {
      displayModeBar: false,
      responsive: true,
      scrollZoom: false, // Disable scroll zoom
      staticPlot: false, // Allow hover interactions but disable other interactions
      showTips: false,   // Disable tips
      doubleClick: false // Disable double click actions
    };
    
    try {
      window.Plotly.newPlot(windChartRef.current, data, layout, config);
      
      // Make sure the chart fits in its container
      const resizeChart = () => {
        // Make sure windChartRef.current exists
        if (!windChartRef.current) return;
        
        // Make sure Plotly is loaded
        if (!window.Plotly) return;
        
        try {
          const container = windChartRef.current.parentElement;
          if (container) {
            const width = container.clientWidth;
            window.Plotly.relayout(windChartRef.current, {
              width: width - 20, // Subtract padding
              height: 280, // Explicitly set height
              'xaxis.automargin': true
            }).catch(err => {
              // Silently catch any Plotly errors during resize
              console.warn('Plotly resize error:', err);
            });
          }
        } catch (err) {
          // Catch any other errors during resize
          console.warn('Chart resize error:', err);
        }
      };
      
      // Initial resize and add listener
      setTimeout(resizeChart, 100); // Delay initial resize
      
      // Use a safe event listener that won't crash if removed multiple times
      const safeResizeChart = () => {
        if (document.body.contains(windChartRef.current)) {
          resizeChart();
        }
      };
      
      window.addEventListener('resize', safeResizeChart);
      
      // Return a cleanup function
      return () => {
        window.removeEventListener('resize', safeResizeChart);
      };
    } catch (err) {
      console.error('Error creating wind chart:', err);
    }
  };

  // For debugging only - show information during initial render
  if (Object.keys(debugInfo).length > 0 && isBrowser) {
    console.log('Weather Forecast Debug Info:', debugInfo);
  }

  if (loading) {
    return (
      <div style={styles.weatherLoading}>
        <p>Loading weather forecast...</p>
      </div>
    );
  }

  if (error) {
    // Return debugging information instead of an empty div
    return (
      <div style={styles.weatherError}>
        <h3>Weather Forecast Error</h3>
        <p>There was an error loading the weather forecast. This could be because:</p>
        <ul>
          <li>The location may be outside the United States (NWS API only covers US territories)</li>
          <li>The NWS API service may be experiencing issues</li>
          <li>The coordinates for this location may be incorrect</li>
        </ul>
        <div style={styles.weatherDebug}>
          <p>Error: {error}</p>
          <p>Coordinates: {latitude}, {longitude}</p>
          <p>Debug Info: {JSON.stringify(debugInfo, null, 2)}</p>
        </div>
      </div>
    );
  }

  // Return nothing if no forecast data is available
  if (!forecastData) return (
    <div style={styles.weatherError}>
      <p>No forecast data available for this location.</p>
      <div style={styles.weatherDebug}>
        <p>Coordinates: {latitude}, {longitude}</p>
        <p>Debug Info: {JSON.stringify(debugInfo, null, 2)}</p>
      </div>
    </div>
  );

  // Include yesterday's data if available
  const combinedForecast = yesterdayData 
    ? [yesterdayData, ...forecastData] 
    : forecastData;

  return (
    <div style={styles.weatherForecastContainer}>
      <h2 style={{ marginBottom: '1.5rem', color: '#d6e1ec' }}>Weather Conditions</h2>
      
      <div style={styles.weatherSection}>
        <div style={styles.weatherCardContainer}>
          {combinedForecast.map((period, index) => {
            // Format wind speed for display
            const formattedWindSpeed = formatWindSpeed(period.windSpeed);
            
            return (
              <div 
                key={index} 
                style={{
                  ...styles.weatherCard,
                  ...(hoveredCardIndex === index ? styles.weatherCardHover : {}),
                  ...(period.isDaytime ? styles.dayCard : styles.nightCard),
                  ...(isMobile ? { width: '110px' } : {})
                }}
                onMouseEnter={() => setHoveredCardIndex(index)}
                onMouseLeave={() => setHoveredCardIndex(null)}
              >
                <div style={styles.weatherDay}>
                  {period.displayTime || period.name}
                </div>
                <div style={styles.weatherIcon}>
                  {getWeatherIcon(period.shortForecast)}
                </div>
                <div style={styles.weatherTemp}>{period.temperature}°F</div>
                <div style={styles.weatherDesc}>
                  {period.shortForecast}
                </div>
                <div style={styles.weatherWind}>
                  <span style={styles.weatherWindHighlight}>{formattedWindSpeed}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Wind Chart */}
      {windData && windData.length > 0 && (
        <div style={{
          ...styles.windChartContainer,
          width: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}>
          <h2 style={{ marginBottom: '1.5rem', color: '#d6e1ec' }}>Wind Forecast</h2>
          <div style={{
            border: '1px solid rgba(255,255,255,0.1)',
            padding: '10px',
            borderRadius: '4px',
            backgroundColor: 'rgba(20,83,116,0.4)',
            marginBottom: '10px',
            width: '100%',
            boxSizing: 'border-box',
            overflow: 'hidden'
          }}>
            <div ref={windChartRef} style={{
              height: '280px',
              width: '100%',
              boxSizing: 'border-box',
              cursor: 'pointer'
            }} />
          </div>
          <div style={{
            fontSize: '12px',
            color: '#d6e1ec',
            opacity: 0.8,
            textAlign: 'center',
            marginBottom: '10px'
          }}>
            Hover over data points for detailed wind information
          </div>
        </div>
      )}
      
      <div style={styles.weatherFooter}>
        <p style={styles.weatherAttribution}>
          Data provided by the National Weather Service
        </p>
      </div>
    </div>
  );
};

export default WeatherForecast; 